// src/pages/po/AdvancedSearchPurchasesKanbanModalContent.tsx
import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@/icons';
import SearchButton from '../../components/common/SearchButton';
import DateRangeInput from '../../components/common/DateRangeInput';
import DateInput from '../../components/common/DateInput';
import { apiGet } from '../../services/api';
import './AdvancedSearchPurchasesKanbanModalContent.css';
import { useApiCall } from '../../hooks/useApiCall';
import { showAlert } from '../../contexts/DialogContext';
import { Toast } from '../../contexts/ToastContext';
import { logger } from '@/utils/logger';

interface AdvancedSearchPurchasesKanbanModalContentProps {
  theme: 'dark' | 'light';
  onClose: () => void;
  onSearch: (result: { __query?: string }) => void; // Callback para retornar os resultados da busca
}

const AdvancedSearchPurchasesKanbanModalContent: React.FC<AdvancedSearchPurchasesKanbanModalContentProps> = ({
  theme,
  onClose,
  onSearch,
}) => {
  const [requisicaoId, setRequisicaoId] = useState(''); // Ajustado de 'id' para 'requisicaoId'
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [dataEntrega, setDataEntrega] = useState('');
  const [dataEncerrada, setDataEncerrada] = useState('');
  const [codProduto, setCodProduto] = useState('');
  const [nomeProduto, setNomeProduto] = useState('');
  const [codCategoria, setCodCategoria] = useState('');
  const [nomeCategoria, setNomeCategoria] = useState('');
  const [prioridade, setPrioridade] = useState('');
  const [loja, setLoja] = useState('');
  const [requisitante, setRequisitante] = useState(''); // Usado como "Requisitante" ou "Comprador(a)"
  const [origem, setOrigem] = useState<'' | 'PLANEJAMENTO' | 'BALCAO'>(''); // Opções de origem para filtro
  const [contato, setContato] = useState('');
  const [nomeConsultor, setnomeConsultor] = useState('');
  const [codCliente, setCodCliente] = useState(''); // Usado como "Código Cliente" ou "Código Fornecedor"
  const [nomeCliente, setNomeCliente] = useState(''); // Usado como "Nome Cliente" ou "Nome Fornecedor"
  const [codFornecedor, setCodFornecedor] = useState(''); // Novo: Código do fornecedor
  const [nomeFornecedor, setNomeFornecedor] = useState(''); // Novo: Nome do fornecedor
  const [preOrdemId, setPreOrdemId] = useState(''); // Novo: ID da pré-ordem
  const [ordemCompraId, setOrdemCompraId] = useState(''); // Novo: ID da ordem de compra
  const { isLoading } = useApiCall();

  const filterValuesRef = useRef({
    requisicaoId: '', // Ajustado de 'id' para 'requisicaoId'
    dataInicio: '',
    dataFim: '',
    dataEntrega: '',
    dataEncerrada: '',
    codProduto: '',
    nomeProduto: '',
    codCategoria: '',
    nomeCategoria: '',
    prioridade: '',
    loja: '',
    requisitante: '',
    origem: '',
    contato: '',
    nomeConsultor: '',
    codCliente: '',
    nomeCliente: '',
    codFornecedor: '', // Novo
    nomeFornecedor: '', // Novo
    preOrdemId: '', // Novo
    ordemCompraId: '', // Novo
  });

  useEffect(() => {
    filterValuesRef.current = {
      requisicaoId, dataInicio, dataFim, dataEntrega, dataEncerrada, codProduto, nomeProduto, codCategoria, nomeCategoria,
      prioridade, loja, requisitante, origem, contato, nomeConsultor, codCliente, nomeCliente,
      codFornecedor, nomeFornecedor, preOrdemId, ordemCompraId // Novos incluídos
    };
  }, [
    requisicaoId, dataInicio, dataFim, dataEntrega, dataEncerrada, codProduto, nomeProduto, codCategoria, nomeCategoria,
    prioridade, loja, requisitante, origem, contato, nomeConsultor, codCliente, nomeCliente,
    codFornecedor, nomeFornecedor, preOrdemId, ordemCompraId // Novos incluídos
  ]);

  const handleSearch = async () => {
    try {
      // Declara baseParams para filtros (sem page/size)
      const baseParams = new URLSearchParams();
      const {
        requisicaoId, dataInicio, dataFim, dataEntrega, dataEncerrada, codProduto, nomeProduto, codCategoria, nomeCategoria,
        prioridade, loja, requisitante, origem, contato, nomeConsultor, codCliente, nomeCliente,
        codFornecedor, nomeFornecedor, preOrdemId, ordemCompraId
      } = filterValuesRef.current;

      const hasFilter =
        requisicaoId.trim() !== '' || dataInicio.trim() !== '' || dataFim.trim() !== '' || dataEntrega.trim() !== '' || dataEncerrada.trim() !== '' ||
        codProduto.trim() !== '' || nomeProduto.trim() !== '' || codCategoria.trim() !== '' ||
        nomeCategoria.trim() !== '' || prioridade.trim() !== '' || loja.trim() !== '' ||
        requisitante.trim() !== '' || origem.trim() !== '' || contato.trim() !== '' || nomeConsultor.trim() !== '' ||
        codCliente.trim() !== '' || nomeCliente.trim() !== '' ||
        codFornecedor.trim() !== '' || nomeFornecedor.trim() !== '' || preOrdemId.trim() !== '' || ordemCompraId.trim() !== '';

      if (!hasFilter) {
        await showAlert('Pelo menos um parâmetro de filtro deve ser fornecido.', { variant: 'warning' });
        return;
      }

      // Adiciona filtros em baseParams
      if (requisicaoId) baseParams.append('requisicaoId', requisicaoId.trim());
      if (dataInicio) baseParams.append('dataInicio', dataInicio.trim());
      if (dataFim) baseParams.append('dataFim', dataFim.trim());
      if (dataEntrega) baseParams.append('dataEntrega', dataEntrega.trim());
      if (dataEncerrada) baseParams.append('dataEncerrada', dataEncerrada.trim());
      if (codProduto) baseParams.append('codProduto', codProduto.trim());
      if (nomeProduto) baseParams.append('nomeProduto', nomeProduto.trim());
      if (codCategoria) baseParams.append('codCategoria', codCategoria.trim());
      if (nomeCategoria) baseParams.append('nomeCategoria', nomeCategoria.trim());
      if (prioridade) baseParams.append('prioridade', prioridade.trim());
      if (loja) baseParams.append('loja', loja.trim());
      if (requisitante) baseParams.append('requisitante', requisitante.trim());
      if (origem) baseParams.append('origem', origem);
      if (contato) baseParams.append('contato', contato.trim());
      if (nomeConsultor) baseParams.append('nomeConsultor', nomeConsultor.trim());
      if (codCliente) baseParams.append('codCliente', codCliente.trim());
      if (nomeCliente) baseParams.append('nomeCliente', nomeCliente.trim());
      if (codFornecedor) baseParams.append('codFornecedor', codFornecedor.trim());
      if (nomeFornecedor) baseParams.append('nomeFornecedor', nomeFornecedor.trim());
      if (preOrdemId) baseParams.append('preOrdemId', preOrdemId.trim());
      if (ordemCompraId) baseParams.append('ordemCompraId', ordemCompraId.trim());
      const queryString = baseParams.toString();

      // Função para buscar todos os IDs de uma seção (acumulação para exportação completa)
      const fetchAllIds = async (section: 'emAberto' | 'finalizadas'): Promise<number[]> => {
        let page = 0;
        const size = 100;
        let allIds: number[] = [];

        while (true) {
          const params = new URLSearchParams(baseParams);
          params.set('page', page.toString());
          params.set('size', size.toString());
          const res = await apiGet(`/v1/busca-inteligente/capa-gestao-compras?${params.toString()}`);
          const sectionData = res.data[section];
          if (!sectionData || !sectionData.content || sectionData.content.length === 0) break;
          allIds = [...allIds, ...sectionData.content.map((item: { id: number }) => item.id)];
          if (page >= (sectionData.page.totalPages - 1)) break;
          page++;
        }
        return allIds;
      };

      // Busca primeira página para exibição no Kanban (page=0, size=10)
      const displayParams = new URLSearchParams(baseParams);
      displayParams.set('page', '0');
      displayParams.set('size', '10');
      const firstRes = await apiGet(`/v1/busca-inteligente/capa-gestao-compras?${displayParams.toString()}`, { blocking: true });
      let result = firstRes.data;

      // Acumula IDs completos para exportação
      const [abertasIds, finalizadasIds] = await Promise.all([
        fetchAllIds('emAberto'),
        fetchAllIds('finalizadas')
      ]);

      // Adiciona allIds ao resultado (compatível com Compras.tsx)
      result = {
        ...result,
        __query: queryString,
        emAberto: { ...result.emAberto, allIds: abertasIds },
        finalizadas: { ...result.finalizadas, allIds: finalizadasIds }
      };

      onSearch(result);
      Toast.success('Pesquisa realizada com sucesso!');
      onClose();
    } catch (err) {
      logger.error('Erro ao buscar:', err);
      await showAlert('Erro ao realizar a busca.', { variant: 'error' });
    }
  };

  return (
    <div className={`advanced-search-purchases-kanban-modal-content ${theme}`}>
      <h2><FontAwesomeIcon icon={faMagnifyingGlass} /> Pesquisa Avançada</h2>
      <div className="search-form">
        <div className="form-inputs">
          <div className="form-row">
            <input
              type="text"
              value={requisicaoId}
              onChange={(e) => setRequisicaoId(e.target.value)}
              placeholder="Requisição"
              className="search-input"
            />
            <DateRangeInput
              dataInicio={dataInicio}
              setDataInicio={setDataInicio}
              dataFim={dataFim}
              setDataFim={setDataFim}
              theme={theme}
            />
            <input
              type="text"
              value={codProduto}
              onChange={(e) => setCodProduto(e.target.value)}
              placeholder="Código Produto"
              className="search-input"
            />
            <input
              type="text"
              value={nomeProduto}
              onChange={(e) => setNomeProduto(e.target.value)}
              placeholder="Nome do Produto"
              className="search-input"
            />
          </div>
          <div className="form-row">
            <input
              type="text"
              value={codCategoria}
              onChange={(e) => setCodCategoria(e.target.value)}
              placeholder="Código Categoria"
              className="search-input"
            />
            <input
              type="text"
              value={nomeCategoria}
              onChange={(e) => setNomeCategoria(e.target.value)}
              placeholder="Categoria"
              className="search-input"
            />
            <input
              type="text"
              value={prioridade}
              onChange={(e) => setPrioridade(e.target.value)}
              placeholder="Prioridade"
              className="search-input"
            />
            <input
              type="text"
              value={loja}
              onChange={(e) => setLoja(e.target.value)}
              placeholder="Loja Solicitante"
              className="search-input"
            />
            <input
              type="text"
              value={requisitante}
              onChange={(e) => setRequisitante(e.target.value)}
              placeholder="Requisitante"
              className="search-input"
            />
          </div>
          <div className="form-row">
            <select
              value={origem}
              onChange={(e) => setOrigem(e.target.value as '' | 'PLANEJAMENTO' | 'BALCAO')}
              className="form-select"
            >
              <option value="">Requisitado por...</option>
              <option value="BALCAO">Balcão</option>
              <option value="PLANEJAMENTO">Planejamento</option>
            </select>
          </div>
          <div className="form-row">
            <input
              type="text"
              value={contato}
              onChange={(e) => setContato(e.target.value)}
              placeholder="Contato"
              className="search-input"
            />
            <input
              type="text"
              value={nomeConsultor}
              onChange={(e) => setnomeConsultor(e.target.value)}
              placeholder="Consultor"
              className="search-input"
            />
            <input
              type="text"
              value={codCliente}
              onChange={(e) => setCodCliente(e.target.value)}
              placeholder="Código Cliente"
              className="search-input"
            />
            <input
              type="text"
              value={nomeCliente}
              onChange={(e) => setNomeCliente(e.target.value)}
              placeholder="Nome Cliente"
              className="search-input"
            />
          </div>
          <div className="form-row">
            <input
              type="text"
              value={preOrdemId}
              onChange={(e) => setPreOrdemId(e.target.value)}
              placeholder="Cotação"
              className="search-input"
            />
            <input
              type="text"
              value={codFornecedor}
              onChange={(e) => setCodFornecedor(e.target.value)}
              placeholder="Código Fornecedor"
              className="search-input"
            />
            <input
              type="text"
              value={nomeFornecedor}
              onChange={(e) => setNomeFornecedor(e.target.value)}
              placeholder="Nome Fornecedor"
              className="search-input"
            />
            <input
              type="text"
              value={ordemCompraId}
              onChange={(e) => setOrdemCompraId(e.target.value)}
              placeholder="Ordem de Compra"
              className="search-input"
            />
          </div>
          <div className="form-row">
            <DateInput
              value={dataEntrega}
              onChange={setDataEntrega}
              placeholder="Data Entrega"
              purpose="Previsão de entrega"
              theme={theme}
            />
            <DateInput
              value={dataEncerrada}
              onChange={setDataEncerrada}
              placeholder="Data Encerrada"
              purpose="Encerrada até"
              theme={theme}
            />
          </div>
        </div>
        <SearchButton isLoading={isLoading} onClick={handleSearch} disabled={isLoading}>
          Procurar
        </SearchButton>
      </div>
    </div>
  );
};

export default AdvancedSearchPurchasesKanbanModalContent;
